import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { DOC_SEARCH_QUERY, useGeckoAnalytics } from 'features/analytics'
import { useDocChatAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import { useDocChatMessageQueue } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatMessageQueue'
import {
  createDocChatTurnId,
  hasInProgressDocChatTurn,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import { submitDocChatMessage } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatMessageQueue'
import {
  consumeTagPicks,
  tagPicksHiddenContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatTagPicks'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useFavoriteSearches } from 'features/knowledgeDocumentViewer/hooks/useFavoriteSearches'
import {
  type DocSearchQueryPowerAnalyticsProperties,
  toDocSearchAnalyticsMode,
  usePowerAnalyticsBackend,
} from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useSearchQueryHistory } from 'features/knowledgeDocumentViewer/hooks/useSearchQueryHistory'
import {
  type DocumentSearchMode,
  type SearchHistoryItem,
  type SearchStateUpdate,
  type ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { useGeckoLocale } from 'features/locale/GeckoLocaleProvider'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DocumentsViewerContentFilter,
  urlSearchParamsFromColumnFilters,
} from 'routes/documents/-searchParams'

export interface SearchSubmitOptions {
  clearInputAfterSubmit?: boolean
  startDocChatTurn?: boolean
}

export interface SearchSubmittedEvent {
  query: string
  options?: SearchSubmitOptions
  previousSearchState: SearchHistoryItem['searchState']
  isLinkedDocsMode: boolean
}

type SearchInputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>

interface AdvancedSearchUXContextType {
  siteId: string
  searchState: SearchHistoryItem['searchState']
  isLinkedDocsMode: boolean
  isActive: boolean

  searchInput: string
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  viewMode: ViewMode

  selectedDocumentId: string | null
  pagination: PaginationState
  sorting: SortingState
  columnFilters: SearchHistoryItem['searchState']['filters']

  onSearchStateChange: (updates: SearchStateUpdate) => void
  handleSearchChange: (e: SearchInputChangeEvent) => void
  handleSearchInputChange: (inputQuery: string) => void
  handleSearchSubmit: (options?: SearchSubmitOptions) => void
  handleSearchKeyDown: (e: React.KeyboardEvent) => void
  handleTabChange: (tab: DocumentsViewerContentFilter) => void
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void

  setPagination: (
    updater: PaginationState | ((prev: PaginationState) => PaginationState)
  ) => void
  setSorting: (
    updater: SortingState | ((prev: SortingState) => SortingState)
  ) => void
  setColumnFilters: (
    updater:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState)
  ) => void
  clearSelectedDocument: () => void
  setSelectedDocumentId: (id: string) => void

  leftPaneWidthPercent: number
  setLeftPaneWidthPercent: React.Dispatch<React.SetStateAction<number>>

  searchSuggestions: SearchQueryHistoryEntry[]
  onSuggestionSelect: (query: string, options?: SearchSubmitOptions) => void
  /** Send the tab's staged tag picks as their own turn, no typing needed. */
  submitStagedTagPicks: () => void
  onClearSearchHistory: () => void
  favoriteSearches: FavoriteSearch[]
  isSearchFavorited: (query: string) => boolean
  toggleSearchFavorite: (query: string, contentFilter: string) => void
  tabId?: string
}

interface AdvancedSearchUXProviderProps {
  children: React.ReactNode
  siteId: string
  searchState: SearchHistoryItem['searchState']
  isLinkedDocsMode?: boolean
  isActive?: boolean
  tabId?: string
  onSearchSubmitted: (event: SearchSubmittedEvent) => void
}

const AdvancedSearchUXContext = createContext<
  AdvancedSearchUXContextType | undefined
>(undefined)

export const AdvancedSearchUXProvider: React.FC<
  AdvancedSearchUXProviderProps
> = ({
  children,
  siteId,
  searchState,
  isLinkedDocsMode = false,
  isActive = true,
  tabId,
  onSearchSubmitted,
}) => {
  const { onSearchStateChange: onSearchStateChangeForActiveTab } =
    useSearchActions()
  const { trackEvent } = useGeckoAnalytics()
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteId)
  const { locale } = useGeckoLocale()
  const { addQuery, getFilteredQueries, recentQueries, clearHistory } =
    useSearchQueryHistory(siteId)
  const { favoriteSearches, isSearchFavorited, toggleSearchFavorite } =
    useFavoriteSearches(siteId)

  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate) => {
      if (tabId) {
        onSearchStateChangeForActiveTab(updates, { tabId })
        return
      }
      onSearchStateChangeForActiveTab(updates)
    },
    [onSearchStateChangeForActiveTab, tabId]
  )

  const [searchInput, setSearchInput] = useState(
    () => searchState.inputQuery ?? searchState.query
  )
  const { pendingAttachments, commitPendingAttachments } =
    useDocChatAttachments()
  const hasSubmittedSearch =
    isLinkedDocsMode ||
    searchState.query.trim().length > 0 ||
    searchState.browseAll === true
  const searchMode =
    hasSubmittedSearch || searchState.chatOnly ? searchState.searchMode : 'ai'
  const [leftPaneWidthPercent, setLeftPaneWidthPercent] = useState(() => {
    try {
      const stored = localStorage.getItem('documentSearch_previewPanelWidth:v1')
      if (stored) {
        const parsed = Number(stored)
        if (!Number.isNaN(parsed) && parsed >= 20 && parsed <= 80) return parsed
      }
    } catch {
      /* fallback to default */
    }
    return 70
  })

  useEffect(() => {
    setSearchInput(searchState.inputQuery ?? searchState.query)
  }, [searchState.inputQuery, searchState.query])

  useEffect(() => {
    try {
      localStorage.setItem(
        'documentSearch_previewPanelWidth:v1',
        String(leftPaneWidthPercent)
      )
    } catch {
      /* localStorage unavailable */
    }
  }, [leftPaneWidthPercent])

  const handleSearchInputChange = useCallback(
    (inputQuery: string) => {
      setSearchInput(inputQuery)
      onSearchStateChange({ inputQuery })
    },
    [onSearchStateChange]
  )

  const handleSearchChange = useCallback(
    (e: SearchInputChangeEvent) => handleSearchInputChange(e.target.value),
    [handleSearchInputChange]
  )

  useDocChatMessageQueue({ searchState, siteId, onSearchStateChange })

  const handleSearchSubmit = useCallback(
    (options?: SearchSubmitOptions) => {
      const q = searchInput.trim()
      const hasPendingAttachments = pendingAttachments.length > 0
      const shouldStartDocChatTurn =
        searchMode === 'ai' && searchState.contentFilter === 'documents'
      // An attachment can carry a turn on its own; text is only required
      // when there is nothing else to send.
      if (!q && shouldStartDocChatTurn && !hasPendingAttachments) {
        return
      }
      const shouldClearInputAfterSubmit =
        options?.clearInputAfterSubmit ?? shouldStartDocChatTurn
      const turnId =
        shouldStartDocChatTurn && (q || hasPendingAttachments)
          ? createDocChatTurnId()
          : undefined
      const consumedPicks = turnId ? consumeTagPicks(tabId) : []
      const hiddenContext = tagPicksHiddenContext(consumedPicks) || undefined
      const tagSelections = consumedPicks.length ? consumedPicks : undefined
      const isBrowseAll = !q && !turnId
      const queryPayload: DocSearchQueryPowerAnalyticsProperties = {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- Stable analytics categories.
        query: q || (turnId ? '(attachments only)' : '(browse all)'),
        browse_all: isBrowseAll,
        content_filter: searchState.contentFilter,
        site_id: siteId,
        search_mode: toDocSearchAnalyticsMode(shouldStartDocChatTurn),
        locale,
        ...urlSearchParamsFromColumnFilters(searchState.filters ?? []),
      }
      trackEvent(DOC_SEARCH_QUERY.title, queryPayload)
      sendPowerAnalyticsEvent(DOC_SEARCH_QUERY.title, queryPayload)
      if (q) {
        addQuery(q, searchState.contentFilter)
      }
      const nextSearchInput = shouldClearInputAfterSubmit ? '' : q
      const inputQuery = shouldClearInputAfterSubmit ? '' : undefined
      onSearchStateChange((currentSearchState) => ({
        ...(!turnId ? { query: q } : {}),
        inputQuery,
        searchMode:
          currentSearchState.contentFilter === 'documents'
            ? searchMode
            : 'normal',
        browseAll: isBrowseAll,
        ...(turnId
          ? submitDocChatMessage(currentSearchState, siteId, {
              id: turnId,
              query: q,
              hiddenContext,
              tagSelections,
              attachments: pendingAttachments.map((attachment) => ({
                id: attachment.id,
                name: attachment.name,
              })),
            })
          : { docChat: undefined }),
        pagination: {
          pageIndex: 0,
          pageSize: currentSearchState.pagination.pageSize,
        },
        selectedDocumentId: null,
      }))
      setSearchInput(nextSearchInput)
      if (shouldStartDocChatTurn) commitPendingAttachments()
      onSearchSubmitted({
        query: q,
        options: {
          ...options,
          startDocChatTurn: shouldStartDocChatTurn,
        },
        previousSearchState: searchState,
        isLinkedDocsMode,
      })
    },
    [
      onSearchStateChange,
      searchInput,
      pendingAttachments,
      commitPendingAttachments,
      searchState,
      tabId,
      trackEvent,
      sendPowerAnalyticsEvent,
      siteId,
      locale,
      addQuery,
      onSearchSubmitted,
      isLinkedDocsMode,
      searchMode,
    ]
  )

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation()
        handleSearchSubmit()
      }
    },
    [handleSearchSubmit]
  )

  const handleTabChange = useCallback(
    (tab: DocumentsViewerContentFilter) => {
      onSearchStateChange({
        contentFilter: tab,
        selectedDocumentId: null,
        inputQuery: '',
        // New surface — drop the previous conversation.
        docChat: undefined,
      })
      setSearchInput('')
    },
    [onSearchStateChange]
  )

  const handleViewModeChange = useCallback(
    (
      value: string | number,
      options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
    ) => {
      if (value === searchState.viewMode) return
      if (
        value === 'table' &&
        options?.selectedDocumentIdWhenSwitchingToTable !== undefined
      ) {
        onSearchStateChange({
          viewMode: 'table',
          selectedDocumentId:
            options.selectedDocumentIdWhenSwitchingToTable ?? null,
        })
      } else {
        onSearchStateChange({ viewMode: value as ViewMode })
      }
    },
    [onSearchStateChange, searchState.viewMode]
  )

  const setPagination = useCallback(
    (
      updater: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => {
      const prev = searchState.pagination
      const next = typeof updater === 'function' ? updater(prev) : updater
      onSearchStateChange({ pagination: next })
    },
    [onSearchStateChange, searchState.pagination]
  )

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const next =
        typeof updater === 'function' ? updater(searchState.sorting) : updater
      onSearchStateChange({ sorting: next })
    },
    [onSearchStateChange, searchState.sorting]
  )

  const setColumnFilters = useCallback(
    (
      updater:
        | ColumnFiltersState
        | ((prev: ColumnFiltersState) => ColumnFiltersState)
    ) => {
      const next =
        typeof updater === 'function' ? updater(searchState.filters) : updater
      onSearchStateChange({ filters: next })
    },
    [onSearchStateChange, searchState.filters]
  )

  const clearSelectedDocument = useCallback(() => {
    onSearchStateChange({ selectedDocumentId: null })
  }, [onSearchStateChange])

  const setSelectedDocumentId = useCallback(
    (id: string) => {
      onSearchStateChange({ selectedDocumentId: id })
    },
    [onSearchStateChange]
  )

  const searchSuggestions = useMemo(() => {
    const trimmed = searchInput.trim()
    if (trimmed) {
      return getFilteredQueries(trimmed)
    }
    return recentQueries()
  }, [searchInput, getFilteredQueries, recentQueries])

  const onSuggestionSelect = useCallback(
    (query: string, options?: SearchSubmitOptions) => {
      const trimmed_query = query.trim()
      const shouldStartDocChatTurn =
        searchMode === 'ai' && searchState.contentFilter === 'documents'
      const shouldClearInputAfterSubmit =
        options?.clearInputAfterSubmit ?? shouldStartDocChatTurn
      const turnId =
        shouldStartDocChatTurn && trimmed_query
          ? createDocChatTurnId()
          : undefined
      const consumedPicks = turnId ? consumeTagPicks(tabId) : []
      const hiddenContext = tagPicksHiddenContext(consumedPicks) || undefined
      const tagSelections = consumedPicks.length ? consumedPicks : undefined
      const nextSearchInput = shouldClearInputAfterSubmit ? '' : trimmed_query
      const inputQuery = shouldClearInputAfterSubmit ? '' : undefined
      setSearchInput(nextSearchInput)
      onSearchStateChange((currentSearchState) => ({
        ...(!turnId ? { query: trimmed_query } : {}),
        inputQuery,
        searchMode:
          currentSearchState.contentFilter === 'documents'
            ? searchMode
            : 'normal',
        browseAll: false,
        ...(turnId
          ? submitDocChatMessage(currentSearchState, siteId, {
              id: turnId,
              query: trimmed_query,
              hiddenContext,
              tagSelections,
            })
          : { docChat: undefined }),
        pagination: {
          pageIndex: 0,
          pageSize: currentSearchState.pagination.pageSize,
        },
        selectedDocumentId: null,
      }))
      addQuery(trimmed_query, searchState.contentFilter)
      onSearchSubmitted({
        query: trimmed_query,
        options: {
          ...options,
          startDocChatTurn: shouldStartDocChatTurn,
        },
        previousSearchState: searchState,
        isLinkedDocsMode,
      })
    },
    [
      setSearchInput,
      onSearchStateChange,
      searchState,
      siteId,
      tabId,
      addQuery,
      onSearchSubmitted,
      isLinkedDocsMode,
      searchMode,
    ]
  )

  const submitStagedTagPicks = useCallback(() => {
    if (searchMode !== 'ai' || searchState.contentFilter !== 'documents') return
    const docChat = searchState.docChat
    if (
      docChat?.siteId === siteId &&
      hasInProgressDocChatTurn(docChat.conversation.turns)
    ) {
      return
    }
    const picks = consumeTagPicks(tabId)
    if (picks.length === 0) return
    const query = picks.map((pick) => pick.label).join(', ')
    const turnId = createDocChatTurnId()
    const hiddenContext = tagPicksHiddenContext(picks) || undefined
    onSearchStateChange((currentSearchState) => ({
      inputQuery: searchInput,
      searchMode,
      browseAll: false,
      ...submitDocChatMessage(currentSearchState, siteId, {
        id: turnId,
        query,
        hiddenContext,
        tagSelections: picks,
      }),
      pagination: {
        pageIndex: 0,
        pageSize: currentSearchState.pagination.pageSize,
      },
      selectedDocumentId: null,
    }))
    onSearchSubmitted({
      query,
      options: { startDocChatTurn: true },
      previousSearchState: searchState,
      isLinkedDocsMode,
    })
  }, [
    searchMode,
    searchState,
    searchInput,
    tabId,
    onSearchStateChange,
    siteId,
    onSearchSubmitted,
    isLinkedDocsMode,
  ])

  const onClearSearchHistory = useCallback(() => {
    clearHistory()
  }, [clearHistory])

  const contextValue = useMemo<AdvancedSearchUXContextType>(
    () => ({
      siteId,
      searchState,
      isLinkedDocsMode,
      isActive,
      searchInput,
      setSearchInput,
      contentFilter: searchState.contentFilter,
      searchMode,
      viewMode: searchState.viewMode,
      selectedDocumentId: searchState.selectedDocumentId ?? null,
      pagination: searchState.pagination,
      sorting: searchState.sorting,
      columnFilters: searchState.filters,
      onSearchStateChange,
      handleSearchChange,
      handleSearchInputChange,
      handleSearchSubmit,
      handleSearchKeyDown,
      handleTabChange,
      handleViewModeChange,
      setPagination,
      setSorting,
      setColumnFilters,
      clearSelectedDocument,
      setSelectedDocumentId,
      leftPaneWidthPercent,
      setLeftPaneWidthPercent,
      searchSuggestions,
      onSuggestionSelect,
      submitStagedTagPicks,
      onClearSearchHistory,
      favoriteSearches,
      isSearchFavorited,
      toggleSearchFavorite,
      tabId,
    }),
    [
      siteId,
      searchState,
      searchMode,
      isLinkedDocsMode,
      isActive,
      searchInput,
      onSearchStateChange,
      handleSearchChange,
      handleSearchInputChange,
      handleSearchSubmit,
      handleSearchKeyDown,
      handleTabChange,
      handleViewModeChange,
      setPagination,
      setSorting,
      setColumnFilters,
      clearSelectedDocument,
      setSelectedDocumentId,
      leftPaneWidthPercent,
      setLeftPaneWidthPercent,
      searchSuggestions,
      onSuggestionSelect,
      submitStagedTagPicks,
      onClearSearchHistory,
      favoriteSearches,
      isSearchFavorited,
      toggleSearchFavorite,
      tabId,
    ]
  )

  return (
    <AdvancedSearchUXContext.Provider value={contextValue}>
      {children}
    </AdvancedSearchUXContext.Provider>
  )
}

export const useAdvancedSearchUX = (): AdvancedSearchUXContextType => {
  const ctx = useContext(AdvancedSearchUXContext)
  if (!ctx) {
    throw new Error(
      'useAdvancedSearchUX must be used within an AdvancedSearchUXProvider'
    )
  }
  return ctx
}
